/**
 * Écran d'échec du déploiement (§3.3).
 *
 * Le message affiché est le `failureReason` renvoyé par l'API : il est rédigé
 * côté serveur pour l'utilisateur final. On l'affiche tel quel, sans le
 * reformuler ni le noyer sous un message générique.
 */

import { STATE_LABELS, type DeploymentState } from '@portail/shared';
import { Copy, RefreshCw, X } from 'lucide-react';

import { useClipboard } from '../hooks/useClipboard';
import { buildErrorReport } from '../lib/error-report';
import { formatDuration } from '../lib/format';
import type { LogEntry } from '../lib/logs';
import { Button, Card, CopyFeedback } from './ui';

/** Propriétés du panneau d'échec. */
export interface FailurePanelProps {
  deploymentId: string;
  slug: string;
  domain: string;
  url: string | null;
  state: DeploymentState;
  failedStep: DeploymentState | null;
  failureReason: string | null;
  createdAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  logs: readonly LogEntry[];
  /** Relance du déploiement avec le même code. */
  onRetry: () => void;
  retrying: boolean;
  retryError: string | null;
}

/** Panneau affiché lorsque le pipeline échoue. */
export function FailurePanel({
  deploymentId,
  slug,
  domain,
  url,
  state,
  failedStep,
  failureReason,
  createdAt,
  finishedAt,
  durationMs,
  logs,
  onRetry,
  retrying,
  retryError,
}: FailurePanelProps) {
  const { status, copy } = useClipboard();

  const copyReport = (): void => {
    copy(
      buildErrorReport({
        deploymentId,
        slug,
        domain,
        url,
        state,
        failedStep,
        failureReason,
        createdAt,
        finishedAt,
        logs,
      }),
    );
  };

  return (
    <Card className="border-red-300 p-6 dark:border-red-900">
      <div className="flex items-start gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 animate-pop-in items-center justify-center rounded-full bg-red-100 dark:bg-red-950">
          <X className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
            {state === 'CANCELLED' ? 'Déploiement annulé' : 'Le déploiement a échoué'}
          </h2>

          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            {failureReason ??
              "Le pipeline s'est interrompu sans motif détaillé. Les logs techniques ci-dessous " +
                'contiennent la trace complète.'}
          </p>

          <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Étape fautive</dt>
              <dd className="font-medium text-slate-900 dark:text-slate-100">
                {failedStep ? `${STATE_LABELS[failedStep]} (${failedStep})` : 'non déterminée'}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Durée avant l'échec</dt>
              <dd className="font-medium text-slate-900 dark:text-slate-100">
                {formatDuration(durationMs)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Application visée</dt>
              <dd className="font-mono text-xs text-slate-900 dark:text-slate-100">
                {slug}.{domain}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Identifiant du job</dt>
              <dd className="font-mono text-xs text-slate-900 dark:text-slate-100">
                {deploymentId}
              </dd>
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button variant="primary" onClick={onRetry} loading={retrying}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Réessayer
            </Button>
            <Button variant="secondary" onClick={copyReport}>
              <Copy className="h-4 w-4" aria-hidden="true" />
              Copier le rapport d'erreur
            </Button>
            <CopyFeedback status={status} />
          </div>

          {retryError ? (
            <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
              {retryError}
            </p>
          ) : null}

          {/* Le code n'est pas modifiable ici : la reprise repart donc de
              l'étape fautive, sans refaire la réécriture ni la construction
              lorsqu'elles avaient abouti. Le dire évite de croire que rien ne
              se passe quand la nouvelle tentative franchit trois étapes en
              deux secondes. */}
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Réessayer relance le déploiement avec le même code, en repartant de l'étape qui a échoué
            : ce qui avait abouti n'est pas refait.
          </p>

          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            Le rapport copié contient l'identifiant du job, l'application visée, l'étape, le motif,
            les horodatages et les 100 dernières lignes de log : collez-le tel quel dans votre
            demande au service informatique.
          </p>
        </div>
      </div>
    </Card>
  );
}
